import { FileUploader, SignedURLUploadRequest, UploadNotificationRequest, UploadRequest } from './file.uploader';
import { MTMCustomFile, TransferType } from './mtm-custom-file';
import { listenerPreUploadCompleted, listenerUploadPendingScan, listenerUploadStatusChanged } from './listeners';
import { TransferProgress } from './transfer-progress';
import { EMPTY, Observable, Subject } from 'rxjs';
import { TransferStatus } from './transfer-status';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { ApiService, UrlSanitizer } from '../api.service';
import { LogService } from '../log.service';

import { Injectable } from '@angular/core';
import { contentTypeRecord, IS_UNCANCELABLE_REQUEST } from '../../models';
import { FileInfo, IHaveFiles, MalwareScanningStatus } from '../../interfaces';
import { MalwareScanMonitorService } from './malware-scan-monitor.service';
import { TransferQueueService } from './transfer-queue-service';
import { SignedUrlUploadPipelineManager, UploadStepHandler } from './signed-url-upload-pipeline-manager';
import { FileUtilsService } from '../file-utils.service';

export interface PreUploadPayload {
  filesContentTypes: Record<string, string>;
  filesSizes: Record<string, number>;
  fileAddedTime: Record<string, number>;
  parentId?: string;
  directory: string;
  files: unknown;
  message: string | null;
}

interface UploadNotificationInfo {
  fileIds: string[];
  id: string;
  referenceId: string;
  success: boolean;
  versionNumber?: number;
}

//SignedURLUploader is used for uploading files to Google Cloud Storage via signed URLs
//Related references:
//https://cloud.google.com/storage/docs/resumable-uploads
//https://cloud.google.com/storage/docs/performing-resumable-uploads
@Injectable({
  providedIn: 'root',
})
export class SignedUrlUploader implements FileUploader {
  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService,
    private readonly scanMonitorService: MalwareScanMonitorService,
    private readonly transferQueue: TransferQueueService,
    private readonly pipelineManager: SignedUrlUploadPipelineManager,
    private readonly fileUtilsService: FileUtilsService,
    private readonly logService: LogService
  ) {}

  /**
   * Initiates file upload process by constructing upload pipelines and calling pre-upload
   * @param request - The upload request containing files and configuration
   */
  public uploadFiles(request: UploadRequest): void {
    this.pipelineManager.constructUploadPipelines(request);
    request.files.forEach((file: MTMCustomFile) => {
      this.handleUploadStep(file);
    });
    this.preUpload(request as SignedURLUploadRequest);
  }

  /**
   * Cancels an ongoing upload and deletes partially uploaded file from Google Cloud Storage if it's a chunked upload
   * @param file - The file to cancel upload for
   * @returns Observable indicating success/failure of cancellation
   */
  public cancelUpload(file: MTMCustomFile): Observable<boolean> {
    if (file.currentRequest) {
      file.currentRequest.unsubscribe();
    }

    if (file.transferType != TransferType.Chunked) {
      return EMPTY;
    }

    //delete the uploaded file from google cloud storage
    const result$: Subject<boolean> = new Subject<boolean>();
    this.http
      .delete(file.sessionUrl, {
        observe: 'response',
        context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
      })
      .subscribe({
        next: () => {
          result$.next(true);
        },
        error: () => {
          result$.next(false);
        },
      });

    return result$.asObservable();
  }

  /**
   * Resumes a paused or interrupted upload
   * @param file - The file to resume upload for
   */
  public resumeUpload(file: MTMCustomFile): void {
    const wasInactive: boolean = file.uploadStatusCode === TransferStatus.INACTIVE;
    file.uploadStatusCode = TransferStatus.SENDING;
    file.retryCount = 0;
    listenerUploadStatusChanged.next(file);

    if (!wasInactive || file.transferType == TransferType.Chunked) {
      this.checkInterruptedUploadInfo(file);
    }
  }

  /**
   * Queries Google Cloud Storage to check existing upload progress
   * @param file - The file to check upload info for
   */
  private checkInterruptedUploadInfo(file: MTMCustomFile): void {
    const locationResponseRe: RegExp = /bytes=(\d+)-(\d+)/;
    const safeUrl: string = UrlSanitizer.sanitizeUrl(file.sessionUrl);
    this.http
      .put(safeUrl, null, {
        headers: {
          'Content-Range': `bytes */${file.fileSize}`,
        },
        observe: 'response',
        context: new HttpContext().set(IS_UNCANCELABLE_REQUEST, true),
      })
      .subscribe({
        next: () => {
          file.progress = 100;
          file.retryCount = 0;
          file.progress$.next({
            status: TransferStatus.UPLOADED,
            startIndex: file.fileSize - 1,
            step: 'next',
          });
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 308) {
            const rangeResp: string = error.headers.get('Range') || '';
            if (rangeResp) {
              const match: RegExpMatchArray | null = rangeResp.match(locationResponseRe);
              if (match && match.length >= 3) {
                file.retryCount = 0;
                const nextStart: number = parseInt(match[2].toString(), 10);
                file.progress$.next({
                  status: TransferStatus.SENDING,
                  startIndex: nextStart,
                  step: '',
                });
                return;
              }
            }
          }
          file.progress$.next({
            status: TransferStatus.SENDING,
            startIndex: 0,
            step: '',
          });
        },
      });
  }

  private handleUploadStep(mtmFile: MTMCustomFile): void {
    if (!mtmFile.progress$) {
      mtmFile.progress$ = new Subject<TransferProgress>();
    }

    const stepHandlers: Map<string, UploadStepHandler> = new Map<string, UploadStepHandler>([
      ['postUpload', (file: MTMCustomFile): void => this.postUpload(file)],
      ['triggerNotification', (file: MTMCustomFile): void => this.triggerNotification(file)],
      ['scanning', (file: MTMCustomFile): void => this.waitForScanResult(file)],
    ]);

    this.pipelineManager.handleUploadStep(mtmFile, stepHandlers);
  }

  /**
   * Sets up batch notification system for multiple file uploads
   * @param request - The upload request containing files for batch notification
   */
  private setupBatchNotification(request: SignedURLUploadRequest): void {
    const preUploadResponse: FileInfo[] = request.files
      .filter((f: MTMCustomFile) => f.dbFileObject)
      .map((f: MTMCustomFile) => f.dbFileObject);
    const notificationRequest: UploadNotificationRequest = {
      objects: preUploadResponse,
      referenceId: request.files[0].entity.id,
    };
    this.apiService.newPost('/api/notifications/batch/files', notificationRequest, { uncancelable: true }).subscribe({
      next: (response: UploadNotificationRequest) => {
        const batchId: string = response.id;
        request.files.forEach((file: MTMCustomFile) => {
          if (!file.dbFileObject) {
            return;
          }
          file.notificationId = batchId;
        });
      },
      error: () => {
        // Handle error
      },
    });
  }

  /**
   * Prepares files for upload by registering them with backend
   * @param request - The upload request to prepare
   */
  private preUpload(request: SignedURLUploadRequest): void {
    const mtmFiles: MTMCustomFile[] = request.files;
    const contentTypes: Record<string, string> = {};
    const filesSizes: Record<string, number> = {};
    const filesAddedTime: Record<string, number> = {};

    mtmFiles.forEach((file: MTMCustomFile) => {
      filesSizes[file.fileName] = file.fileSize;
      filesAddedTime[file.fileName] = file.createTime || +new Date();
      const extension: string = this.fileUtilsService.getFileExtension(file.fileName);
      if (extension && extension in contentTypeRecord) {
        contentTypes[file.fileName] = contentTypeRecord[extension];
      } else {
        contentTypes[file.fileName] = file.fileType || file.type || (file as any).contentType;
      }
    });

    const payload: PreUploadPayload = {
      filesContentTypes: contentTypes,
      filesSizes: filesSizes,
      fileAddedTime: filesAddedTime,
      parentId: request.parentId || '00000000-0000-0000-0000-000000000000',
      directory: '/',
      files: null,
      message: null,
    };

    const safeurl: string = UrlSanitizer.sanitizeUrl(request.preUploadUrl);

    this.apiService.newPost(safeurl, payload, { uncancelable: true }).subscribe({
      next: (response: FileInfo[]) => {
        response.forEach((dbElement: FileInfo) => {
          const index: number = mtmFiles.findIndex((item: MTMCustomFile) => item.fileName === dbElement.displayName);
          if (index == -1) {
            return;
          }
          const mtmFile: MTMCustomFile = mtmFiles[index];
          mtmFile.setDbFileObject(dbElement);
          mtmFile.preUploadUrl = request.preUploadUrl;

          if (mtmFile.extraHandlers?.['previewUrl']) {
            try {
              mtmFile.extraInfo.previewUrl = mtmFile.extraHandlers['previewUrl'](dbElement);
              delete mtmFile.extraHandlers['previewUrl'];
            } catch (ex) {
              this.logService.logMessage('warning', ex, { context: 'previewUrl handler' });
            }
          }

          listenerPreUploadCompleted.emit(mtmFile);

          mtmFile.uploadStatusCode = TransferStatus.SENDING;
          mtmFile.startIndex = 0;
          mtmFile.progress = 0;
          listenerUploadStatusChanged.emit(mtmFile);
          this.transferQueue.enqueueUpload({
            file: mtmFile,
            uploadFn: () => {
              mtmFile.progress$.next({
                status: TransferStatus.SENDING,
                startIndex: 0,
                step: 'next',
              });
            },
          });
        });

        if (response.length > 0) {
          this.setupBatchNotification(request);
        }
      },
      error: (err: HttpErrorResponse) => {
        mtmFiles.forEach((mtmFile: MTMCustomFile) => {
          mtmFile.progress$.error({
            error: err,
            handled: true,
          });
        });
      },
    });
  }

  /**
   * Notifies backend of completion
   * @param file - The file to finalize upload for
   */
  private postUpload(file: MTMCustomFile): void {
    let payload: string[] = [];

    if (!file.postUploadPayload) {
      payload = [file.dbFileObject.id];
    } else {
      if (typeof file.postUploadPayload === 'string') {
        payload = JSON.parse(file.postUploadPayload) as string[];
      } else {
        payload = file.postUploadPayload;
      }
    }

    const safeurl: string = UrlSanitizer.sanitizeUrl(file.postUploadUrl);

    file.currentRequest = this.apiService.newPost(safeurl, payload, { uncancelable: true }).subscribe({
      next: (response: IHaveFiles | FileInfo[]) => {
        this.handlePostUploadResponse(file, response);

        const newStatus: string = TransferStatus.SCANNING;
        listenerUploadStatusChanged.emit(file);
        file.uploadStatusCode = newStatus;
        listenerUploadPendingScan.emit(file);
        file.progress$.next({
          status: newStatus,
          startIndex: file.startIndex,
          step: 'next',
        });
      },
      error: (err: HttpErrorResponse) => {
        file.progress$.error({
          error: err,
        });
      },
    });
  }

  private handlePostUploadResponse(file: MTMCustomFile, response: IHaveFiles | FileInfo[]): void {
    try {
      let fileRef: FileInfo | null = null;
      if ('files' in response && response.files?.length) {
        fileRef = response.files.find((f: FileInfo) => f.id == file.dbFileObject.id);
      } else if (Array.isArray(response) && response.length == 1) {
        fileRef = response[0].id == file.dbFileObject.id ? response[0] : null;
      }
      if (fileRef) {
        file.setDbFileObject(fileRef);
      }
      if (file.postUploadAttributes) {
        file.setPostUploadAttributes(response);
      }
    } catch (e) {
      this.logService.logMessage('warning', e, { context: 'handlePostUploadResponse' });
    }
  }

  /**
   * Sends batch notification to backend about upload completion
   * @param file - The file to trigger notification for
   */
  private triggerNotification(file: MTMCustomFile): void {
    const relevantStatusCodes: string[] = [TransferStatus.COMPLETED, TransferStatus.ERROR];
    if (!relevantStatusCodes.includes(file.uploadStatusCode) || !file.notificationId) {
      file.progress$.next({
        status: file.uploadStatusCode,
        startIndex: file.startIndex,
        step: 'next',
      });
      return;
    }

    const requestBody: UploadNotificationInfo = {
      fileIds: [file.dbFileObject.id],
      id: file.notificationId,
      referenceId: file.entity?.id,
      success: !!(file.uploadStatusCode == TransferStatus.COMPLETED),
    };

    if (file.entity?.version) {
      requestBody.versionNumber = file.entity.version;
    }

    this.apiService
      .newPut(`/api/notifications/batch/files/${file.notificationId}`, requestBody, { uncancelable: true })
      .subscribe({
        next: () => {
          if (file.uploadStatusCode != TransferStatus.ERROR) {
            file.progress$.next({
              status: file.uploadStatusCode,
              startIndex: file.startIndex,
              step: 'next',
            });
          }
        },
        error: () => {
          if (file.uploadStatusCode != TransferStatus.ERROR) {
            file.progress$.next({
              status: file.uploadStatusCode,
              startIndex: file.startIndex,
              step: 'next',
            });
          }
        },
      });
  }

  /**
   * Monitors malware scanning status after upload completion
   * @param file - The file to monitor scan results for
   */
  private waitForScanResult(file: MTMCustomFile): void {
    file.uploadStatusCode = TransferStatus.SCANNING;
    const update$: Observable<any> = this.scanMonitorService.monitorFile({
      fileId: file.dbFileObject.id,
      projectId: file.dbFileObject.projectId,
    });
    file.scanUpdateSubscription = update$.subscribe({
      next: (data) => {
        if (!data) {
          return;
        }
        this.fileUtilsService.copyFilePropsAfterScanning(data, file.dbFileObject);
        switch (data?.malwareScanningStatus) {
          case MalwareScanningStatus.Clean:
            file.uploadStatusCode = TransferStatus.COMPLETED;
            file.progress$.next({
              status: TransferStatus.COMPLETED,
              startIndex: file.fileSize,
              step: 'next',
            });
            file.scanUpdateSubscription.unsubscribe();
            break;
          case MalwareScanningStatus.Quarantined:
            file.uploadStatusCode = TransferStatus.QUARANTINED;
            file.progress$.next({
              status: TransferStatus.QUARANTINED,
              startIndex: file.fileSize,
              step: 'next',
            });
            file.scanUpdateSubscription.unsubscribe();
            break;
        }
      },
    });
  }
}
